/**
 * Domain hook for Maintenance module data management
 * Do not import in non-maintenance modules to avoid cross-module writes
 * 
 * Uses canonical snake_case keys:
 * - maintenance_history: for maintenance records
 * - equipment_records: for equipment data
 * 
 * Handles one-time migration from legacy camelCase keys
 */

import { useEffect } from 'react';
import { devLog } from '../../utils/devLog';
import { useStorageState } from '../useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { useMaintenanceHistory } from './maintenance/useMaintenanceHistory';
import { useDefects } from './defects/useDefects';
import { usePermissions } from '../usePermissions';
import { useEquipmentRegister } from './useEquipmentRegister';
import { useAuditTrail } from '../useAuditTrail';
// FT-267 §16: normalise the resolved category in audit moduleSpecificData so
// the audit trail records a consistent canonical code regardless of which
// form path produced it.
import { normaliseCategory } from '../../config/maintenanceRequirements-wrapper';
// FT-221: Removed useCrew/resolveCrewName - name now passed directly from form
import { serviceTypeFromJob } from '../../services/maintenance/serviceType';
import * as storage from '../../lib/storage';
import { 
  readLegacyMaintenance, 
  clearLegacyMaintenance,
  getMigrationFlag,
  setMigrationFlag 
} from '../../utils/migrations/maintenanceMigration';
import {
  normalizeHistory,
  needsMigration,
  ensureId,
  safeMap,
  shallowEqualArrays,
  normalizeRecord,
  getDefaultPerformer
} from './maintenance/utils';

// Version marker for debugging
devLog('[useMaintenanceData] v2025-09-01 13:00Z - GPT-approved fixes');

// Canonical snake_case keys (no new keys!)
const HISTORY_KEY = 'maintenance_history';
const EQUIPMENT_KEY = 'equipment_records';

// ID generator
const makeId = () => (crypto?.randomUUID?.() ?? `${Date.now()}_${Math.random().toString(36).slice(2)}`);

// Helper to calculate next due date based on interval
const addIntervalToDate = (isoDate, interval, unit) => {
  if (!interval || !unit) return null;
  
  const date = new Date(isoDate);
  
  // Normalize unit to handle singular/plural
  const normalizedUnit = unit.toLowerCase().replace(/s$/, '');
  
  switch (normalizedUnit) {
    case 'day':
      date.setDate(date.getDate() + interval);
      break;
    case 'week':
      date.setDate(date.getDate() + (interval * 7));
      break;
    case 'month':
      date.setMonth(date.getMonth() + interval);
      break;
    case 'year':
      date.setFullYear(date.getFullYear() + interval);
      break;
    default:
      return null;
  }
  
  return date.toISOString();
};

export function useMaintenanceData() {
  const { getCurrentVessel } = useVessel();
  const vessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();
  // FT-221: Removed useCrew() - name now passed directly from form as inspectorName

  devLog('[useMaintenanceData] Vessel:', vessel?.id);

  // Use clean facades for both history and defects
  const { history, addCompletedCheck, getByEquipmentType, getHistoryForEquipmentId, getLastCompleted, signOffCompletion } = useMaintenanceHistory();
  const { addDefect } = useDefects();

  // FT-267: audit every maintenance completion (routine path).
  // Ad-hoc completions are audited at their call site (PlannedMaintenanceTab modal save).
  const { logChange } = useAuditTrail('maintenance');
  
  // Keep equipment separate for now (should have its own facade later)
  const { value: rawEquipment = [], setValue: setEquipment } = useStorageState(EQUIPMENT_KEY, []);
  
  // Always normalize to ensure we have arrays
  const equipment = normalizeHistory(rawEquipment);
  
  devLog('[useMaintenanceData] History loaded:', history.length, 'items');
  
  // Equipment migration only (history is handled by facade)
  useEffect(() => {
    if (!vessel?.id) return;
    if (getMigrationFlag(vessel.id)) return;
    
    const legacy = readLegacyMaintenance(vessel.id);
    
    // Migrate equipment with deduplication
    if (legacy.equipment?.length > 0) {
      setEquipment(prev => {
        const normalized = normalizeHistory(prev);
        const byId = new Map(normalized.map(x => {
          const id = ensureId(x);
          return [id, { ...x, id }];
        }));
        
        legacy.equipment.forEach(x => {
          const id = ensureId(x);
          byId.set(id, { ...x, id });
        });
        
        const next = Array.from(byId.values());
        if (shallowEqualArrays(normalized, next)) return prev;
        return next;
      });
    }
    
    // Clean up legacy keys and mark as migrated
    clearLegacyMaintenance(vessel.id);
    setMigrationFlag(vessel.id);
  }, [vessel?.id, setEquipment]);
  

  /**
   * Complete a maintenance job with optional defect creation
   * Creates defect FIRST using facade, then links it to maintenance history
   * Also updates equipment records with service dates
   */
  const completeMaintenanceJob = ({ fields = {}, alsoCreateDefect = false, performedBy, equipmentIds, job }) => {
    devLog('[completeMaintenanceJob] Starting with:', {
      equipmentType: fields?.equipmentType,
      alsoCreateDefect,
      hasDefects: fields?.hasDefects,
      defectsFound: fields?.defectsFound,
      defectData: fields?.defectData,
      performedBy
    });
    
    let defectId = null;
    let historyId = null;
    
    // RBAC shim to support multiple permission signatures
    const canCreateDefects = () => {
      try {
        // Try multiple signatures for compatibility
        if (can && typeof can === 'function') {
          if (can.length >= 2 && can('defects', 'create')) return true;
          if (can('defects:create')) return true;
          if (can({ resource: 'defects', action: 'create' })) return true;
          if (can('create', 'defects')) return true;
        }
      } catch {}
      return false;
    };
    
    // Create defect FIRST if needed (with permission check)
    const hasPermission = canCreateDefects();
    devLog('[completeMaintenanceJob] Permission check for create defects:', hasPermission);
    
    if (alsoCreateDefect && hasPermission) {
      devLog('[completeMaintenanceJob] ✅ Creating defect now...');
      
      // Use the defects facade to create defect with canonical fields
      const defectInput = {
        // Map modal data FIRST (if available)
        title: fields?.defectData?.description || fields?.defectDetails || fields?.title,
        description: fields?.defectData?.description || fields?.defectDetails || fields?.title,  // Use description field!
        severity: fields?.defectData?.severity || fields?.defectSeverity,
        priority: fields?.defectData?.priority || fields?.defectPriority,
        category: fields?.defectData?.category,
        notes: fields?.defectData?.notes || fields?.notes,
        
        // Equipment and linkage context
        equipment_affected: fields?.equipmentType,
        jobTitle: fields?.title,
        jobId: fields?.jobId,
        equipmentType: fields?.equipmentType,
        
        // Who reported
        reported_by: performedBy || fields?.inspector,
        reportedBy: performedBy || fields?.inspector,
        inspector: fields?.inspector,
        
        // Source
        source: 'maintenance'
      };
      
      devLog('[completeMaintenanceJob] 📋 Creating defect with input:', defectInput);
      defectId = addDefect(defectInput);
      devLog('[completeMaintenanceJob] ✅ Defect created with ID:', defectId);
      
      // The defect facade handles the storage, we trust it's saved
      // Can't verify directly due to ESLint rules against localStorage access
    } else if (alsoCreateDefect && !hasPermission) {

    } else {
      devLog('[completeMaintenanceJob] ℹ️ No defect creation requested');
    }
    
    // Then write maintenance history (with linkedDefectId if defect was created)
    // FT-267: tag every routine completion with completionType so future records
    // are self-describing and the migration block can distinguish them from ad-hoc.
    // FT-349a: allocate completionId upfront so the history record and the
    // job.completions[] entry share the same stable id. If the caller (e.g. the
    // FT-349b form) supplied one, thread it through; otherwise mint one here.
    const completionId = fields?.completionId || makeId();
    const checkFields = defectId
      ? { ...fields, completionId, linkedDefectId: defectId, completionType: 'routine' }
      : { ...fields, completionId, completionType: 'routine' };
    // FT-221: Use inspectorName passed from form (direct read from crew object at selection time)
    // Do NOT use resolveCrewName() - crew list may not be loaded at write time
    const performerId = performedBy || fields?.inspector;
    const performerName = fields?.inspectorName || null;  // Direct from form, no lookup

    // FT-267 §15: resolve equipment names at write time (FT-221 pattern for equipment)
    // `equipment` is the live equipment register in scope from useStorageState above.
    const resolvedEquipmentIds = equipmentIds || job?.linkedEquipmentIds || [];
    const resolvedEquipmentNames = resolvedEquipmentIds
      .map(id => {
        const eq = equipment.find(e => e.id === id);
        return eq?.name || eq?.item || eq?.label || null;
      })
      .filter(Boolean);

    historyId = addCompletedCheck({
      fields: checkFields,
      performer: performerId,
      performerName: performerName,  // FT-221: Save display name for history view
      equipmentIds: resolvedEquipmentIds,
      equipmentNames: resolvedEquipmentNames,  // FT-267 §15: stored at write time, FT-221 pattern for equipment
      job,  // FT-353e: pass through so addCompletedCheck can inherit job.originatingDefectId
    });

    // FT-267: audit the routine maintenance completion. Fire-and-forget per
    // VesselSettingsView precedent — failure must not block the completion UX.
    logChange({
      action: 'maintenance_completed',
      item: {
        id: historyId,
        type: 'maintenance_completion',
        name: fields?.title || fields?.equipmentType || 'Maintenance Completion',
        category: fields?.category || null,
      },
      reason: { code: 'completed', label: 'Scheduled maintenance completed' },
      moduleSpecificData: {
        completionType: 'routine',
        jobId: fields?.jobId || null,
        equipmentIds: resolvedEquipmentIds,
        equipmentNames: resolvedEquipmentNames,  // FT-267 §15: audit-trail stability, FT-221 pattern
        // FT-267 §16: store the canonical category code in the audit log so
        // downstream consumers see a consistent value regardless of which
        // form path (LogMaintenance / AddJob) originated the completion.
        category: normaliseCategory(fields?.category),
        date: fields?.date || null,
        defectsFound: !!fields?.defectsFound,
        linkedDefectId: defectId || null,
        performedBy: performerId || null,
        performerName: performerName || null,
      },
    }).catch(err => console.warn('[completeMaintenanceJob] audit write failed (non-fatal):', err));
    
    // BUG-005 FIX: Write completion to JOB layer, not equipment layer
    // Equipment should be static - jobs own their completion state
    // See DOMAIN_SPINE.md Section 5: Maintenance Completion Tracking Pattern
    const affectedEquipmentIds = equipmentIds || job?.linkedEquipmentIds || [];
    const now = new Date().toISOString();

    // FT-23 Fix: Use the completion date from the form, not today
    // This allows backdating completions and calculating nextDue correctly
    const completionDate = fields?.completedAt || fields?.date || now;

    // Calculate nextDue for the completion - from the completion date, not today
    let nextDue = null;
    if (job?.interval && job?.unit) {
      nextDue = addIntervalToDate(completionDate, job.interval, job.unit);
    }

    // Create completion entry for job layer
    // FT-140 FIX: Include both `date` (user-entered) AND `completedAt` (system timestamp)
    // summarizeJob() checks `date` first, then falls back to `completedAt`
    // FT-349a: `id` + `photos` land on the completion so each check owns its own
    // evidence set. `id` is shared with the maintenance_history record (see
    // completionId above) so downstream views can cross-reference.
    const completionEntry = {
      id: completionId,                                   // FT-349a: stable id shared with history record
      date: fields?.date || completionDate.split('T')[0], // FT-140: User-entered date (YYYY-MM-DD format)
      completedAt: completionDate,                        // System timestamp for audit
      performedBy: performerId || 'Unknown',              // Crew ID for linking
      performerName: performerName,                       // FT-221: Display name for history view
      equipmentIds: affectedEquipmentIds,
      nextDue: nextDue,
      notes: fields?.notes || '',
      // FT-349a: photos move from job.photos → completion.photos (per-check).
      photos: Array.isArray(fields?.photos) ? fields.photos : [],
      result: fields?.result || 'Pass',

      // v4.2 Audit Trail (camelCase) - FT-06b: Object format
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      createdAt: now,
      updatedBy: null,
      updatedAt: null
    };

    devLog('[completeMaintenanceJob] Creating job completion:', {
      jobId: job?.id,
      equipmentIds: affectedEquipmentIds,
      nextDue
    });

    // FT-140 DEBUG: Log condition check to diagnose why completions aren't written
    devLog('[completeMaintenanceJob] 🔍 FT-140 DEBUG: Checking write conditions:', {
      hasJobId: !!job?.id,
      jobId: job?.id,
      hasVesselId: !!vessel?.id,
      vesselId: vessel?.id,
      willWrite: !!(job?.id && vessel?.id)
    });

    // Write to job.completions[] using storage (Bug #5 fix - emits sync events)
    if (job?.id && vessel?.id) {
      try {
        const jobsKey = `maintenance_jobs_${vessel.id}`;
        const savedJobs = storage.readJSON(jobsKey) || [];

        const updatedJobs = savedJobs.map(savedJob => {
          if (savedJob.id === job.id) {
            // FT-225: If one-time job, archive it on completion
            const isOneTimeComplete = job.isOneTime === true;

            return {
              ...savedJob,
              completions: [
                ...(savedJob.completions || []),
                completionEntry
              ],
              // FT-225: Auto-archive one-time corrective jobs
              ...(isOneTimeComplete ? {
                status: 'archived',
                archivedAt: new Date().toISOString(),
                archivedBy: currentUser?.name || 'System'
              } : {})
            };
          }
          return savedJob;
        });

        // If job wasn't found in saved jobs, add it with the completion
        const jobExists = savedJobs.some(j => j.id === job.id);
        if (!jobExists) {
          // FT-225: New one-time jobs also get archived on completion
          const isOneTimeComplete = job.isOneTime === true;

          updatedJobs.push({
            ...job,
            completions: [completionEntry],
            ...(isOneTimeComplete ? {
              status: 'archived',
              archivedAt: new Date().toISOString(),
              archivedBy: currentUser?.name || 'System'
            } : {})
          });
        }

        storage.writeJSON(jobsKey, updatedJobs); // ✅ EMITS sr:storage:set EVENT

        devLog('[completeMaintenanceJob] ✅ Completion saved to job layer:', {
          jobId: job.id,
          completionCount: updatedJobs.find(j => j.id === job.id)?.completions?.length || 1
        });

        // Dispatch event so PlannedMaintenanceTab can react
        window.dispatchEvent(new CustomEvent('sr:jobs:updated', {
          detail: { vesselId: vessel.id, jobId: job.id }
        }));

      } catch (err) {
        // FT-47: Log errors instead of silent swallowing - debugging blind without this
        console.error('[completeMaintenanceJob] ❌ Failed to write completion to job layer:', err.message);
      }
    }

    // FT-142 FIX: Write service status to equipment_register (single source of truth)
    // Equipment record owns its own lastServiced/nextServiceDue - no cross-referencing needed
    if (affectedEquipmentIds.length > 0 && vessel?.id) {
      try {
        const equipmentKey = `equipment_register_${vessel.id}`;
        const currentEquipment = storage.readJSON(equipmentKey) || [];

        const updatedEquipment = currentEquipment.map(item => {
          if (affectedEquipmentIds.includes(item.id)) {
            return {
              ...item,
              lastServiced: completionDate,
              lastServicedBy: performedBy || fields?.inspector || 'Unknown',
              nextServiceDue: nextDue,
              lastServiceJobId: job?.id,
              updatedAt: now
            };
          }
          return item;
        });

        storage.writeJSON(equipmentKey, updatedEquipment); // ✅ EMITS sr:storage:set EVENT

        devLog('[completeMaintenanceJob] ✅ Equipment records updated:', {
          equipmentIds: affectedEquipmentIds,
          lastServiced: completionDate,
          nextServiceDue: nextDue
        });

        // Emit event for equipment register to refresh
        window.dispatchEvent(new CustomEvent('sr:equipment:updated', {
          detail: { vesselId: vessel.id, equipmentIds: affectedEquipmentIds }
        }));

      } catch (err) {
        console.error('[completeMaintenanceJob] ❌ Failed to update equipment records:', err.message);
      }
    }
    
    // FT-353e: expose the linked defect id (explicit OR inherited from the
    // corrective job) so the UI layer can prompt "Close linked defect?" after
    // a successful save. Prefer the just-created defectId when we made one,
    // otherwise fall back to whatever addCompletedCheck resolved (i.e. the
    // corrective job's originatingDefectId).
    const linkedDefectId = defectId || job?.originatingDefectId || null;

    devLog('[completeMaintenanceJob] Complete - historyId:', historyId, 'defectId:', defectId, 'linkedDefectId:', linkedDefectId);
    return { historyId, defectId, linkedDefectId };  // FT-353e: linkedDefectId for UI prompt
  };
  
  return {
    vesselId: vessel?.id ?? '',
    history,  // From facade, already normalized
    equipment: Array.isArray(equipment) ? equipment : [],
    getByEquipmentType,  // From facade
    getHistoryForEquipmentId,  // NEW: From facade
    getLastCompleted,  // From facade
    completeMaintenanceJob,  // Updated to use facade
    signOffCompletion  // FT-394: sign-off state machine
  };
}